import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    Divider,
} from "@mui/material";
import { Search as SearchIcon, Clear as ClearIcon } from "@mui/icons-material";
import { OrderSearchParams, Customer, Product } from "../../types";
import { CustomerSearchAutocomplete } from "../common/CustomerSearchAutocomplete";
import { ProductSearchAutocomplete } from "../common/ProductSearchAutocomplete";
import { customerService } from "../../services/customerService";
import { productService } from "../../services/productService";
import { userService, UserBasic } from "../../services/userService";
import {
    orderChannelService,
    OrderChannelSimple,
} from "../../services/orderChannelService";
import {
    deliveryService,
    DeliveryServiceSimple,
} from "../../services/deliveryService";

interface OrderAdvancedSearchProps {
    open: boolean;
    onClose: () => void;
    onSearch: (params: OrderSearchParams) => void;
    initialParams: OrderSearchParams;
}

export const OrderAdvancedSearch: React.FC<OrderAdvancedSearchProps> = ({
    open,
    onClose,
    onSearch,
    initialParams,
}) => {
    const [searchParams, setSearchParams] =
        useState<OrderSearchParams>(initialParams);
    const [selectedCustomers, setSelectedCustomers] = useState<Customer[]>([]);
    const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
    const [orderChannels, setOrderChannels] = useState<OrderChannelSimple[]>(
        [],
    );
    const [deliveryServices, setDeliveryServices] = useState<
        DeliveryServiceSimple[]
    >([]);
    const [staffUsers, setStaffUsers] = useState<UserBasic[]>([]);

    // Load channel/service/staff lists once when the dialog opens — used
    // to populate the "specific" dropdown for each type-then-specific pair,
    // and the Created By dropdown.
    useEffect(() => {
        if (!open) return;
        orderChannelService
            .getOrderChannelsSimple(true)
            .then(setOrderChannels)
            .catch(() => setOrderChannels([]));
        deliveryService
            .getDeliveryServicesSimple(true)
            .then(setDeliveryServices)
            .catch(() => setDeliveryServices([]));
        userService
            .listUsers()
            .then(setStaffUsers)
            .catch(() => setStaffUsers([]));
    }, [open]);

    useEffect(() => {
        setSearchParams(initialParams);

        const loadInitialData = async () => {
            // Load customers
            if (
                initialParams.customer_ids &&
                initialParams.customer_ids.length > 0
            ) {
                try {
                    const promises = initialParams.customer_ids.map((id) =>
                        customerService.getCustomerById(id).catch(() => null)
                    );
                    const results = await Promise.all(promises);
                    setSelectedCustomers(
                        results.filter((c): c is Customer => c !== null)
                    );
                } catch (error) {
                    console.error("Failed to load customers:", error);
                }
            } else if (initialParams.customer_id) {
                try {
                    const customer = await customerService.getCustomerById(
                        initialParams.customer_id
                    );
                    setSelectedCustomers([customer]);
                } catch (error) {
                    console.error("Failed to load customer:", error);
                }
            } else {
                setSelectedCustomers([]);
            }

            // Load products
            if (
                initialParams.product_ids &&
                initialParams.product_ids.length > 0
            ) {
                try {
                    const promises = initialParams.product_ids.map((id) =>
                        productService.getProductById(id).catch(() => null)
                    );
                    const results = await Promise.all(promises);
                    setSelectedProducts(
                        results.filter((p): p is Product => p !== null)
                    );
                } catch (error) {
                    console.error("Failed to load products:", error);
                }
            } else if (initialParams.product_id) {
                try {
                    const product = await productService.getProductById(
                        initialParams.product_id
                    );
                    setSelectedProducts([product]);
                } catch (error) {
                    console.error("Failed to load product:", error);
                }
            } else {
                setSelectedProducts([]);
            }
        };

        if (open) {
            loadInitialData();
        }
    }, [initialParams, open]);

    const handleChange = (field: keyof OrderSearchParams, value: any) => {
        setSearchParams((prev) => ({
            ...prev,
            [field]: value === "" ? undefined : value,
        }));
    };

    const handleCustomerChange = (customers: Customer | Customer[] | null) => {
        if (Array.isArray(customers)) {
            setSelectedCustomers(customers);
            handleChange(
                "customer_ids",
                customers.map((c) => c.customer_id)
            );
            handleChange("customer_id", undefined); // clear legacy
        } else {
            // Should not happen in multiple mode
            setSelectedCustomers(customers ? [customers] : []);
            handleChange(
                "customer_ids",
                customers ? [customers.customer_id] : undefined
            );
        }
    };

    const handleProductChange = (products: Product | Product[] | null) => {
        if (Array.isArray(products)) {
            setSelectedProducts(products);
            handleChange(
                "product_ids",
                products.map((p) => p.product_id)
            );
            handleChange("product_id", undefined); // clear legacy
        } else {
            setSelectedProducts(products ? [products] : []);
            handleChange(
                "product_ids",
                products ? [products.product_id] : undefined
            );
        }
    };

    const handleSearch = () => {
        onSearch({
            ...searchParams,
            customer_ids: searchParams.customer_ids,
            product_ids: searchParams.product_ids,
            skip: 0, // Reset to first page
        });
        onClose();
    };

    const handleClear = () => {
        const clearedParams: OrderSearchParams = {
            skip: 0,
            limit: searchParams.limit || 50,
            sort_by: "order_date",
            sort_order: "desc",
        };
        setSearchParams(clearedParams);
        setSelectedCustomers([]);
        setSelectedProducts([]);
        onSearch(clearedParams);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Advanced Order Search</DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2 }}>
                    {/* Status Filters */}
                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                        Order Status
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Order Status</InputLabel>
                                <Select
                                    value={searchParams.status || ""}
                                    label="Order Status"
                                    onChange={(e) =>
                                        handleChange("status", e.target.value)
                                    }
                                >
                                    <MenuItem value="">All Statuses</MenuItem>
                                    <MenuItem value="DRAFT">Draft</MenuItem>
                                    <MenuItem value="PENDING">Pending</MenuItem>
                                    <MenuItem value="CONFIRMED">
                                        Confirmed
                                    </MenuItem>
                                    <MenuItem value="SHIPPED">Shipped</MenuItem>
                                    <MenuItem value="DELIVERED">
                                        Delivered
                                    </MenuItem>
                                    <MenuItem value="CANCELLED">
                                        Cancelled
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Payment Status</InputLabel>
                                <Select
                                    value={searchParams.payment_status || ""}
                                    label="Payment Status"
                                    onChange={(e) =>
                                        handleChange(
                                            "payment_status",
                                            e.target.value
                                        )
                                    }
                                >
                                    <MenuItem value="">
                                        All Payment Statuses
                                    </MenuItem>
                                    <MenuItem value="PAID">Paid</MenuItem>
                                    <MenuItem value="PARTIAL">
                                        Partially Paid
                                    </MenuItem>
                                    <MenuItem value="UNPAID">Unpaid</MenuItem>
                                    <MenuItem value="OVERPAID">
                                        Overpaid
                                    </MenuItem>
                                    <MenuItem value="REFUNDED">
                                        Refunded
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    {/* Customer & Product Filters */}
                    <Typography variant="subtitle2" gutterBottom>
                        Customer & Product
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <CustomerSearchAutocomplete
                                multiple
                                value={selectedCustomers}
                                onChange={handleCustomerChange}
                                label="Filter by Customers"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <ProductSearchAutocomplete
                                multiple
                                value={selectedProducts}
                                onChange={handleProductChange}
                                label="Filter by Products"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Created By</InputLabel>
                                <Select
                                    value={searchParams.created_by_user_id || ""}
                                    label="Created By"
                                    onChange={(e) =>
                                        handleChange(
                                            "created_by_user_id",
                                            e.target.value,
                                        )
                                    }
                                >
                                    <MenuItem value="">Anyone</MenuItem>
                                    {staffUsers.map((u) => (
                                        <MenuItem key={u.user_id} value={u.user_id}>
                                            {u.email}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    {/* Channel & Fulfillment Filters */}
                    <Typography variant="subtitle2" gutterBottom>
                        Channel &amp; Fulfillment
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Order Channel Type</InputLabel>
                                <Select
                                    value={searchParams.order_channel_type || ""}
                                    label="Order Channel Type"
                                    onChange={(e) => {
                                        handleChange(
                                            "order_channel_type",
                                            e.target.value,
                                        );
                                        // Selected channel may no longer match
                                        // the new type — clear it rather than
                                        // leave an inconsistent combination.
                                        handleChange(
                                            "order_channel_id",
                                            undefined,
                                        );
                                    }}
                                >
                                    <MenuItem value="">Any Type</MenuItem>
                                    <MenuItem value="OFFLINE">Offline</MenuItem>
                                    <MenuItem value="ONLINE">Online</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Order Channel</InputLabel>
                                <Select
                                    value={searchParams.order_channel_id || ""}
                                    label="Order Channel"
                                    onChange={(e) =>
                                        handleChange(
                                            "order_channel_id",
                                            e.target.value,
                                        )
                                    }
                                >
                                    <MenuItem value="">Any Channel</MenuItem>
                                    {orderChannels
                                        .filter(
                                            (c) =>
                                                !searchParams.order_channel_type ||
                                                c.type ===
                                                    searchParams.order_channel_type,
                                        )
                                        .map((c) => (
                                            <MenuItem
                                                key={c.order_channel_id}
                                                value={c.order_channel_id}
                                            >
                                                {c.name}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Delivery Service Type</InputLabel>
                                <Select
                                    value={
                                        searchParams.delivery_service_type || ""
                                    }
                                    label="Delivery Service Type"
                                    onChange={(e) => {
                                        handleChange(
                                            "delivery_service_type",
                                            e.target.value,
                                        );
                                        handleChange(
                                            "delivery_service_id",
                                            undefined,
                                        );
                                    }}
                                >
                                    <MenuItem value="">Any Type</MenuItem>
                                    <MenuItem value="INHOUSE">In-house</MenuItem>
                                    <MenuItem value="THIRD_PARTY">
                                        Third Party
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Delivery Service</InputLabel>
                                <Select
                                    value={
                                        searchParams.delivery_service_id || ""
                                    }
                                    label="Delivery Service"
                                    onChange={(e) =>
                                        handleChange(
                                            "delivery_service_id",
                                            e.target.value,
                                        )
                                    }
                                >
                                    <MenuItem value="">Any Service</MenuItem>
                                    {deliveryServices
                                        .filter(
                                            (s) =>
                                                !searchParams.delivery_service_type ||
                                                s.type ===
                                                    searchParams.delivery_service_type,
                                        )
                                        .map((s) => (
                                            <MenuItem
                                                key={s.delivery_service_id}
                                                value={s.delivery_service_id}
                                            >
                                                {s.name}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Gift Items</InputLabel>
                                <Select<"" | "yes" | "no">
                                    value={
                                        searchParams.has_gift_items === undefined
                                            ? ""
                                            : searchParams.has_gift_items
                                              ? "yes"
                                              : "no"
                                    }
                                    label="Gift Items"
                                    onChange={(e) =>
                                        handleChange(
                                            "has_gift_items",
                                            e.target.value === ""
                                                ? undefined
                                                : e.target.value === "yes",
                                        )
                                    }
                                >
                                    <MenuItem value="">Any</MenuItem>
                                    <MenuItem value="yes">
                                        Contains Gift Items
                                    </MenuItem>
                                    <MenuItem value="no">
                                        No Gift Items
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    {/* Date Ranges */}
                    <Typography variant="subtitle2" gutterBottom>
                        Order Date Range
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="From Date"
                                type="date"
                                value={searchParams.order_date_from || ""}
                                onChange={(e) =>
                                    handleChange(
                                        "order_date_from",
                                        e.target.value
                                    )
                                }
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="To Date"
                                type="date"
                                value={searchParams.order_date_to || ""}
                                onChange={(e) =>
                                    handleChange(
                                        "order_date_to",
                                        e.target.value
                                    )
                                }
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    </Grid>

                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                        Shipped Date Range
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="From Date"
                                type="date"
                                value={searchParams.shipped_date_from || ""}
                                onChange={(e) =>
                                    handleChange(
                                        "shipped_date_from",
                                        e.target.value
                                    )
                                }
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="To Date"
                                type="date"
                                value={searchParams.shipped_date_to || ""}
                                onChange={(e) =>
                                    handleChange(
                                        "shipped_date_to",
                                        e.target.value
                                    )
                                }
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    </Grid>

                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                        Delivered Date Range
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="From Date"
                                type="date"
                                value={searchParams.delivered_date_from || ""}
                                onChange={(e) =>
                                    handleChange(
                                        "delivered_date_from",
                                        e.target.value
                                    )
                                }
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="To Date"
                                type="date"
                                value={searchParams.delivered_date_to || ""}
                                onChange={(e) =>
                                    handleChange(
                                        "delivered_date_to",
                                        e.target.value
                                    )
                                }
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    {/* Amount Range */}
                    <Typography variant="subtitle2" gutterBottom>
                        Total Amount Range
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Minimum Amount"
                                type="number"
                                value={searchParams.total_amount_min || ""}
                                onChange={(e) =>
                                    handleChange(
                                        "total_amount_min",
                                        e.target.value
                                            ? parseFloat(e.target.value)
                                            : undefined
                                    )
                                }
                                InputProps={{
                                    inputProps: { min: 0, step: 0.01 },
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Maximum Amount"
                                type="number"
                                value={searchParams.total_amount_max || ""}
                                onChange={(e) =>
                                    handleChange(
                                        "total_amount_max",
                                        e.target.value
                                            ? parseFloat(e.target.value)
                                            : undefined
                                    )
                                }
                                InputProps={{
                                    inputProps: { min: 0, step: 0.01 },
                                }}
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 3 }} />

                    {/* Sorting */}
                    <Typography variant="subtitle2" gutterBottom>
                        Sort Results
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Sort By</InputLabel>
                                <Select
                                    value={searchParams.sort_by || "order_date"}
                                    label="Sort By"
                                    onChange={(e) =>
                                        handleChange("sort_by", e.target.value)
                                    }
                                >
                                    <MenuItem value="order_date">
                                        Order Date
                                    </MenuItem>
                                    <MenuItem value="total_amount">
                                        Total Amount
                                    </MenuItem>
                                    <MenuItem value="order_number">
                                        Order Number
                                    </MenuItem>
                                    <MenuItem value="status">Status</MenuItem>
                                    <MenuItem value="customer_name">
                                        Customer Name
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Sort Order</InputLabel>
                                <Select
                                    value={searchParams.sort_order || "desc"}
                                    label="Sort Order"
                                    onChange={(e) =>
                                        handleChange(
                                            "sort_order",
                                            e.target.value
                                        )
                                    }
                                >
                                    <MenuItem value="asc">Ascending</MenuItem>
                                    <MenuItem value="desc">Descending</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit">
                    Cancel
                </Button>
                <Button
                    onClick={handleClear}
                    startIcon={<ClearIcon />}
                    color="warning"
                >
                    Clear All
                </Button>
                <Button
                    onClick={handleSearch}
                    variant="contained"
                    startIcon={<SearchIcon />}
                >
                    Search
                </Button>
            </DialogActions>
        </Dialog>
    );
};
